import type { SupabaseLikeClient } from "./coachInfluenceContext.ts";
import type { ContextSelectionManifest } from "./contextAssemblyReceipt.ts";
import { loadPracticeContext } from "./practiceSelectionContext.ts";

/** Only admitted native choices constrain a derived turn. Suggestions create no authority. */
export async function revalidateAdmittedPractices(
  client: SupabaseLikeClient,
  leaderId: string | null | undefined,
  manifest: ContextSelectionManifest,
): Promise<
  { status: 409 | 503; error: "context_changed" | "context_unavailable" } | null
> {
  const expected = (manifest.bindings ?? []).filter((binding) =>
    binding.kind === "practice_selection"
  );
  if (!expected.length) return null;
  if (!leaderId) return { status: 503, error: "context_unavailable" };
  const current = await loadPracticeContext(client, leaderId, [
    ...new Set(expected.map((binding) => binding.id)),
  ]);
  if (current.status === "unavailable") {
    return { status: 503, error: "context_unavailable" };
  }
  if (
    expected.some((binding) =>
      !current.items.some((item) =>
        item.state === "chosen" && item.selection_id === binding.id &&
        item.revision === binding.revision
      )
    )
  ) {
    return { status: 409, error: "context_changed" };
  }
  return null;
}

/** Includes null and withheld pack identities: selected family also governs
 * correction scope even when no catalog text survives prompt screening. */
export async function revalidateResolvedFrameworkPack(
  expected: import('./frameworkPackDb.ts').DbFrameworkPack | null,
  loadCurrent:()=>Promise<import('./frameworkPackDb.ts').DbFrameworkPack|null>,
):Promise<{status:409|503;error:'context_changed'|'context_unavailable'}|null>{
  try{
    const current=await loadCurrent();
    return current?.id!==expected?.id || current?.family!==expected?.family
      || current?.contentVersion!==expected?.contentVersion
      ? {status:409,error:'context_changed'}:null;
  }catch{return {status:503,error:'context_unavailable'};}
}

/** Final assembly, rather than BI presence, proves that pack content was used. */
export function frameworkPackWasAdmitted(manifest: ContextSelectionManifest): boolean {
  return Boolean(manifest.bindings?.some(binding => binding.kind === 'framework_pack'));
}
